import type { AppState, FamilyExpeditionProgress216, TodayVillageProgress } from './models';
import { addIglooBricks215 } from './iglooGrowth215';
import { awardPuzzlePiece } from './todayVillage';

export const FAMILY_EXPEDITION_POINTS_216 = 25;
export const FAMILY_EXPEDITION_BRICKS_216 = 1;
export const FAMILY_EXPEDITION_PUZZLE_PIECES_216 = 1;

const DAY = 86_400_000;
const KOREA_OFFSET_MS = 9 * 60 * 60 * 1000;

function mondayStart(input: Date): Date {
  const koreaClock = new Date(input.getTime() + KOREA_OFFSET_MS);
  koreaClock.setUTCHours(0, 0, 0, 0);
  const day = koreaClock.getUTCDay();
  koreaClock.setUTCDate(koreaClock.getUTCDate() - (day === 0 ? 6 : day - 1));
  return new Date(koreaClock.getTime() - KOREA_OFFSET_MS);
}

function koreaDateKey(input: Date): string {
  return new Date(input.getTime() + KOREA_OFFSET_MS).toISOString().slice(0, 10);
}

function inCurrentWeek(value: string | undefined, date: Date): boolean {
  if (!value) return false;
  const start = mondayStart(date).getTime();
  const end = start + 7 * DAY;
  const time = new Date(value).getTime();
  return Number.isFinite(time) && time >= start && time < end;
}

export function familyWeekKey216(date = new Date()): string {
  return koreaDateKey(mondayStart(date));
}

export function createFamilyExpeditionProgress216(childId: string, date = new Date()): FamilyExpeditionProgress216 {
  const now = date.toISOString();
  return {
    childId,
    weekKey: familyWeekKey216(date),
    meetingNote: '',
    rewardClaimed: false,
    completedWeeks: [],
    totalFamilyBricksEarned: 0,
    totalPuzzlePiecesEarned: 0,
    updatedAt: now,
  };
}

export function normalizeFamilyExpedition216(
  input: Partial<FamilyExpeditionProgress216> | undefined,
  childId: string,
  date = new Date(),
): FamilyExpeditionProgress216 {
  const base = createFamilyExpeditionProgress216(childId, date);
  if (!input) return base;
  const currentWeek = familyWeekKey216(date);
  const sameWeek = input.weekKey === currentWeek;
  const completedWeeks = Array.from(new Set(Array.isArray(input.completedWeeks) ? input.completedWeeks : []));
  return {
    ...base,
    ...input,
    childId,
    weekKey: currentWeek,
    meetingNote: sameWeek ? String(input.meetingNote ?? '') : '',
    meetingRecordedAt: sameWeek ? input.meetingRecordedAt : undefined,
    rewardClaimed: completedWeeks.includes(currentWeek) || (sameWeek && Boolean(input.rewardClaimed)),
    completedWeeks,
    totalFamilyBricksEarned: Math.max(0, Number(input.totalFamilyBricksEarned ?? 0)),
    totalPuzzlePiecesEarned: Math.max(0, Number(input.totalPuzzlePiecesEarned ?? 0)),
    updatedAt: input.updatedAt || base.updatedAt,
  };
}

export interface FamilyExpeditionSignals216 {
  dailyExploration: boolean;
  parentCheer: boolean;
  familyMission: boolean;
  familyMeeting: boolean;
  completedCount: number;
  complete: boolean;
}

export function buildFamilyExpeditionSignals216(
  state: AppState,
  progress = state.familyExpedition,
  date = new Date(),
): FamilyExpeditionSignals216 {
  const normalized = normalizeFamilyExpedition216(progress, state.child.id, date);
  const dailyExploration = state.todayVillage.rewardClaimed && inCurrentWeek(`${state.todayVillage.contentDate}T12:00:00`, date);
  const parentCheer = state.messages.some((item) => inCurrentWeek(item.createdAt, date));
  const familyMission = state.familyMissionChallenges.some((item) =>
    ['approved', 'completed'].includes(item.status)
    && inCurrentWeek(item.reviewedAt ?? item.submittedAt ?? item.updatedAt, date));
  const familyMeeting = Boolean(normalized.meetingNote.trim() && inCurrentWeek(normalized.meetingRecordedAt, date));
  const completedCount = [dailyExploration, parentCheer, familyMission, familyMeeting].filter(Boolean).length;
  return { dailyExploration, parentCheer, familyMission, familyMeeting, completedCount, complete: completedCount === 4 };
}

export function recordFamilyMeeting216(
  progress: FamilyExpeditionProgress216 | undefined,
  childId: string,
  note: string,
  date = new Date(),
): FamilyExpeditionProgress216 {
  const normalized = normalizeFamilyExpedition216(progress, childId, date);
  const trimmed = note.trim();
  if (!trimmed) return normalized;
  return { ...normalized, meetingNote: trimmed, meetingRecordedAt: date.toISOString(), updatedAt: date.toISOString() };
}

export function claimFamilyExpeditionReward216(
  todayVillage: TodayVillageProgress,
  progress: FamilyExpeditionProgress216 | undefined,
  childId: string,
  signals: FamilyExpeditionSignals216,
  date = new Date(),
): { todayVillage: TodayVillageProgress; familyExpedition: FamilyExpeditionProgress216 } {
  const normalized = normalizeFamilyExpedition216(progress, childId, date);
  if (!signals.complete || normalized.rewardClaimed || normalized.completedWeeks.includes(normalized.weekKey)) {
    return { todayVillage, familyExpedition: normalized };
  }
  const igloo = addIglooBricks215(todayVillage.igloo, 'family', FAMILY_EXPEDITION_BRICKS_216, date.toISOString());
  let puzzle = todayVillage.puzzle;
  for (let index = 0; index < FAMILY_EXPEDITION_PUZZLE_PIECES_216; index += 1) {
    puzzle = awardPuzzlePiece(puzzle, '가족 탐험대 주간 완주', date.toISOString());
  }
  const completedWeeks = [...normalized.completedWeeks, normalized.weekKey];
  return {
    todayVillage: {
      ...todayVillage,
      points: todayVillage.points + FAMILY_EXPEDITION_POINTS_216,
      totalBricks: igloo.totalBricks,
      igloo,
      puzzle,
      updatedAt: date.toISOString(),
    },
    familyExpedition: {
      ...normalized,
      rewardClaimed: true,
      completedWeeks,
      totalFamilyBricksEarned: normalized.totalFamilyBricksEarned + FAMILY_EXPEDITION_BRICKS_216,
      totalPuzzlePiecesEarned: normalized.totalPuzzlePiecesEarned + FAMILY_EXPEDITION_PUZZLE_PIECES_216,
      updatedAt: date.toISOString(),
    },
  };
}
